import * as PIXI from 'pixi.js';

import { assertString } from '../validators';
import { FONT_STATUS } from '../constants';
import { ErrorReporter } from '../libs/error-reporter';
import { bytesToBase64 } from '../libs/base64';
import { toCdnUrl } from '../libs/cdnUrl';
import type { UpdatableLifecycleAware } from '../types';
import type { AssetNetworkManager } from './AssetNetworkManager';
import { getGlobalAssetNetworkManager } from './AssetNetworkManager';
import { FALLBACK_FONT_FIRST_COVERAGE } from './fallbackFontCoverage.generated';
import { FontLoader } from './FontLoader';

declare global {
    interface Window {
        fontManager: FontManager | null;
    }
}

type FontManagerSettings = {
    assetNetworkManager: AssetNetworkManager;
};

const FONT_MIME_BY_EXTENSION: Record<string, string> = {
    ttf: 'font/ttf',
    otf: 'font/otf',
    woff: 'font/woff',
    woff2: 'font/woff2',
};

/**
 * The `font/*` MIME type for a font URL, for building `data:` URIs — some font decoders reject a
 * generic type like the server's `application/octet-stream`. Defaults to `font/ttf`.
 */
export function fontMimeType(url: string): string {
    const path = url.split(/[?#]/)[0];
    const extension = path.substring(path.lastIndexOf('.') + 1).toLowerCase();
    return FONT_MIME_BY_EXTENSION[extension] ?? 'font/ttf';
}

type FontManagerDestroyOptions = {
    keepFallbackFonts?: boolean;
};

/**
 * Retrieves the global font manager.
 * We should load the font manager as a GLOBAL object because we depend on font loading so much we
 * want to re-use as much loaded data as possible and not reload fonts.
 * To use the font loader, simply use window.fontManager
 */
export function getGlobalFontManager(): FontManager {
    // Note we check .classType instead of using instanceof because instanceof doesn't work well
    // across different contexts https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/instanceof#Description
    // and was causing unexpected behaviour in tests
    if (!window.fontManager || window.fontManager.classType !== 'FontManager') {
        // setup fontManager
        const assetNetworkManager = getGlobalAssetNetworkManager();
        window.fontManager = new FontManager({ assetNetworkManager });
    }
    return window.fontManager!;
}

/**
 * Clears the global font manager. If removing fallback fonts then the font manager is also removed,
 * otherwise the fallback fonts are kept and the manager is just cleared of other loaded fonts.
 * Primarily for use between tests to ensure that the global font manager doesn't pollute tests
 */
export function clearGlobalFontManager({ keepFallbackFonts = false } = {}) {
    if (!window.fontManager) {
        return;
    }
    window.fontManager.destroy({ keepFallbackFonts });
    if (!keepFallbackFonts) {
        window.fontManager = null;
    }
}

/**
 * List of fallback urls for fonts. The order of fonts in this list, is the order that
 * the fallback urls will be tried.
 * When updating this list, make sure node rendering follows by updating fallback-fonts.txt
 */
export const FALLBACK_FONT_URLS = [
    // Bulgarian
    // Czech
    // Danish
    // German
    // Greek
    // English
    // Spanish
    // Finnish
    // French
    // Croatian
    // Hungarian
    // Indonesian
    // Italian
    // Macedonian
    // Malay
    // Dutch
    // Norwegian Bokmål
    // Norwegian
    // Polish
    // Portugese
    // Romanian
    // Russian
    // Slovak
    // Serbian
    // Swedish
    // Turkish
    // Ukranian
    // Vietnamese
    'https://storage.googleapis.com/lumen5-site-css/NotoSans-Medium.ttf',
    // Arabic
    'https://storage.googleapis.com/lumen5-site-css/NotoKufiArabic-Medium.ttf',
    'https://storage.googleapis.com/lumen5-site-css/NotoNaskhArabic-Medium.ttf',
    // Hebrew
    'https://storage.googleapis.com/lumen5-site-css/NotoSansHebrew-Medium.ttf',
    // Hindi
    'https://storage.googleapis.com/lumen5-site-css/NotoSansDevanagari-Medium.ttf',
    // Tamil
    'https://storage.googleapis.com/lumen5-site-css/NotoSansTamil-Medium.ttf',
    // Japanese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansJP-Medium.otf',
    // Korean
    'https://storage.googleapis.com/lumen5-site-css/NotoSansKR-Medium.otf',
    'https://storage.googleapis.com/lumen5-site-css/NotoSansKR-Bold.otf', // The Bold font has additional glyphs (row 0000 to 0020) that are not in the Medium font. This was added as a quick workaround to pass some tests. Ideally we should have a better handling for whitespaces. See https://lumen5.atlassian.net/browse/LU-2284
    // Thai
    'https://storage.googleapis.com/lumen5-site-css/NotoSansThai-Medium.ttf',
    // Tagalog
    'https://storage.googleapis.com/lumen5-site-css/NotoSansTagalog-Regular.ttf',
    // Simplified Chinese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansSC-Medium.otf',
    // Traditional Chinese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansTC-Medium.otf',
    // Armenian
    'https://storage.googleapis.com/lumen5-site-css/NotoSansArmenian-Regular.ttf',
    // Georgian
    'https://storage.googleapis.com/lumen5-site-css/DejaVuSans.ttf',
    // Emoji
    'https://storage.googleapis.com/lumen5-site-css/NotoEmoji-Regular.ttf',
    // Gujarati - not well-supported LU-2989
    // 'https://storage.googleapis.com/lumen5-site-css/NotoSansGujarati-Regular.ttf',
    // Bengali - not well-supported see LU-2944 & LU-2945
    // 'https://storage.googleapis.com/lumen5-site-css/NotoSansBengali-Regular.ttf',
];

let fallbackFontRanges: Map<string, Uint32Array> | null = null;

/**
 * For each fallback font, the [start, end, start, end, ...] code point ranges it is the first
 * fallback to cover, decoded on first use from the generated table.
 */
function getFallbackFontRanges(): Map<string, Uint32Array> {
    if (!fallbackFontRanges) {
        fallbackFontRanges = new Map(
            FALLBACK_FONT_URLS.map((url) => {
                const deltas = (FALLBACK_FONT_FIRST_COVERAGE[url] ?? '')
                    .split(',')
                    .filter(Boolean)
                    .map((delta) => parseInt(delta, 36));
                const ranges = new Uint32Array(deltas.length);
                let previousEnd = -1;
                for (let i = 0; i < deltas.length; i += 2) {
                    ranges[i] = previousEnd + deltas[i];
                    ranges[i + 1] = ranges[i] + deltas[i + 1];
                    previousEnd = ranges[i + 1];
                }
                return [url, ranges];
            }),
        );
    }
    return fallbackFontRanges;
}

function rangesContain(ranges: Uint32Array, codePoint: number): boolean {
    let low = 0;
    let high = ranges.length / 2 - 1;
    while (low <= high) {
        const mid = (low + high) >> 1;
        if (codePoint < ranges[mid * 2]) {
            high = mid - 1;
        } else if (codePoint > ranges[mid * 2 + 1]) {
            low = mid + 1;
        } else {
            return true;
        }
    }
    return false;
}

/**
 * The fallback font the browser would draw this character with when the text's own font lacks
 * it: the first fallback in the stack that has it.
 */
export function fallbackFontUrlForCodePoint(codePoint: number): string | null {
    for (const [url, ranges] of getFallbackFontRanges()) {
        if (rangesContain(ranges, codePoint)) {
            return url;
        }
    }
    return null;
}

const MAX_CACHED_TEXTS = 1000;

/**
 * Canvas text preparation replaces ASCII whitespace with spaces before choosing glyphs, so these
 * are drawn with the space glyph of the text's own font. Only NotoSansKR-Bold covers them, and
 * fetching its 4.6MB for every line break would draw nothing differently.
 */
const CANVAS_SPACE_CHARACTERS = new Set(['\t', '\n', '\f', '\r']);
const fallbackFontUrlsByText = new Map<string, readonly string[]>();

/**
 * The fallback fonts needed to draw `text`, in stack order. A fallback is only worth loading when
 * the text has a character it would be the one to draw: the CJK fallbacks alone are about 28MB.
 */
export function fallbackFontUrlsForText(text: string): readonly string[] {
    const cached = fallbackFontUrlsByText.get(text);
    if (cached) {
        return cached;
    }
    const needed = new Set<string>();
    for (const character of text) {
        if (CANVAS_SPACE_CHARACTERS.has(character)) {
            continue;
        }
        const url = fallbackFontUrlForCodePoint(character.codePointAt(0)!);
        if (url) {
            needed.add(url);
        }
    }
    const urls = FALLBACK_FONT_URLS.filter((url) => needed.has(url));
    if (fallbackFontUrlsByText.size >= MAX_CACHED_TEXTS) {
        fallbackFontUrlsByText.clear();
    }
    fallbackFontUrlsByText.set(text, urls);
    return urls;
}

/**
 * A manager class to load and track Fonts which are used by Luminary. All fonts are loaded using the assetNetworkManager.
 */
export class FontManager extends PIXI.EventEmitter implements UpdatableLifecycleAware {
    classType = 'FontManager';
    autoUpdate = false;
    destroyed = false;

    #fonts: Map<string, FontLoader> = new Map();
    #assetNetworkManager: AssetNetworkManager;
    #fontDataUriByUrl: Map<string, string> = new Map();
    #fontDataUriPromiseByUrl: Map<string, Promise<string | null>> = new Map();

    constructor(settings: FontManagerSettings) {
        super();
        this.#assetNetworkManager = settings.assetNetworkManager;
    }

    /**
     * Returns a list of all of the loaded fonts
     * @returns list of loaded fonts
     */
    get fonts(): readonly string[] {
        return Array.from(this.#fonts.keys());
    }

    /**
     * Returns true if the given sourceUrl is a fallback font
     */
    isFallbackUrl(sourceUrl: string): boolean {
        return FALLBACK_FONT_URLS.indexOf(sourceUrl) !== -1;
    }

    /**
     * Returns the FontLoader for the given sourceUrl if the font has loaded.
     */
    getFontBySourceIfLoaded(sourceUrl: string): FontLoader | null {
        const font = this.#fonts.get(sourceUrl);
        if (font && font.isLoaded) {
            return font;
        }
        return null;
    }

    isFontDoneTryingToLoad(sourceUrl: string): boolean {
        const font = this.#fonts.get(sourceUrl);
        if (!font) {
            return false;
        }
        return font.isDoneTryingToLoad;
    }

    /**
     * Returns the FontLoader for the given sourceUrl if the font exists
     * // TODO make internal to FontManager
     */
    getFontBySource(sourceUrl: string): FontLoader | null {
        if (!this.#fonts.has(sourceUrl)) {
            return null;
        }
        return this.#fonts.get(sourceUrl)!;
    }

    /**
     * Returns a promise for the given sourceUrl which resolves once it has loaded or failed to load; check
     * `failedToLoad` on the font it resolves with.
     */
    async getFontBySourceOnceLoaded(sourceUrl: string): Promise<FontLoader> {
        if (!this.getFontBySource(sourceUrl)) {
            this.addFont(sourceUrl);
        }
        const font = this.getFontBySource(sourceUrl)!;
        if (!font.isDoneTryingToLoad) {
            await new Promise<void>((resolve) => font.once('doneTryingToLoad', () => resolve()));
        }
        return font;
    }

    /**
     * The CSS family name for a font URL, without creating a FontLoader or loading the font — for
     * callers that reference a font by name but provision the bytes themselves.
     */
    familyNameForUrl(sourceUrl: string): string {
        return FontLoader.familyNameForUrl(sourceUrl);
    }

    /**
     * The font's bytes as a `data:` URI, fetched once and cached. Resolves null (caching nothing)
     * on failure, so a caller degrades to a fallback and retries later.
     */
    async getFontDataUri(sourceUrl: string): Promise<string | null> {
        const cached = this.#fontDataUriByUrl.get(sourceUrl);
        if (cached !== undefined) {
            return cached;
        }
        let promise = this.#fontDataUriPromiseByUrl.get(sourceUrl);
        if (promise === undefined) {
            promise = this.#fetchFontDataUri(sourceUrl);
            this.#fontDataUriPromiseByUrl.set(sourceUrl, promise);
        }
        return promise;
    }

    /** The already-fetched data URI for a font URL, or null if it isn't cached yet. Synchronous. */
    getCachedFontDataUri(sourceUrl: string): string | null {
        return this.#fontDataUriByUrl.get(sourceUrl) ?? null;
    }

    async #fetchFontDataUri(sourceUrl: string): Promise<string | null> {
        try {
            const response = await fetch(toCdnUrl(sourceUrl));
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const bytes = new Uint8Array(await response.arrayBuffer());
            const dataUri = `data:${fontMimeType(sourceUrl)};base64,${bytesToBase64(bytes)}`;
            this.#fontDataUriByUrl.set(sourceUrl, dataUri);
            return dataUri;
        } catch (error) {
            console.error(`Failed to fetch font bytes ${sourceUrl}`, error);
            // Leave nothing cached so a transient failure retries on the next request.
            this.#fontDataUriPromiseByUrl.delete(sourceUrl);
            return null;
        }
    }

    /**
     * Add a new FontLoader from a ttf/otf url if it hasn't been added yet.
     */
    addFont(sourceUrl: string): FontLoader {
        assertString(sourceUrl);
        if (this.#fonts.has(sourceUrl)) {
            return this.#fonts.get(sourceUrl)!;
        }
        const font = new FontLoader({
            parent: this,
            sourceUrl,
        });
        this.#fonts.set(sourceUrl, font);

        this.#assetNetworkManager.waitForNetworkSlot(font, (requestFinished) => {
            font.load()
                .catch((e) => {
                    console.error(`Failed to load font ${sourceUrl}`, e);
                    ErrorReporter.capture(e, {
                        extra: {
                            sourceUrl,
                        },
                    });
                })
                .finally(() => {
                    requestFinished();
                    const isFallback = this.isFallbackUrl(sourceUrl);
                    if (isFallback) {
                        this.#loadedFallbackFamiliesCss = null;
                    }
                    if (font.status !== FONT_STATUS.FAILED) {
                        this.emit('loaded', sourceUrl);
                        font.emit('loaded');
                    } else if (isFallback) {
                        // Text waiting on this fallback can draw now, just without it
                        this.emit('fallbackFontFailed', sourceUrl);
                    }
                    font.emit('doneTryingToLoad');
                });
        });

        return font;
    }

    /**
     * Removes the internal fonts map.
     */
    destroy({ keepFallbackFonts }: FontManagerDestroyOptions = {}) {
        this.removeAllFonts({ keepFallbackFonts });
        this.removeAllListeners();
        this.destroyed = true;
    }

    removeAllFonts({ keepFallbackFonts }: FontManagerDestroyOptions = {}) {
        this.#fonts.forEach((font, sourceUrl) => {
            if (keepFallbackFonts && this.isFallbackUrl(sourceUrl)) {
                return;
            }
            font.destroy();
            this.#fonts.delete(sourceUrl);
        });
        this.#loadedFallbackFamiliesCss = null;
        // Embedded fonts (data URIs) are never fallback fonts, so they clear unconditionally.
        this.#fontDataUriByUrl.clear();
        this.#fontDataUriPromiseByUrl.clear();
    }

    getFullNameOfLoadedFont(fontUrl: string): string | null {
        const font = this.getFontBySourceIfLoaded(fontUrl);
        if (!font) {
            return null;
        }
        return font.fullName;
    }

    #getLoadedFallbackFontFamilyNames(): string[] {
        return FALLBACK_FONT_URLS.reduce<string[]>((acc, url) => {
            const font = this.getFontBySourceIfLoaded(url);
            if (!font) {
                return acc;
            }
            acc.push(font.familyName);
            return acc;
        }, []);
    }

    #loadedFallbackFamiliesCss: string | null = null;

    getLoadedFontFamilyNamesCssString(): string {
        this.#loadedFallbackFamiliesCss ??= this.#getLoadedFallbackFontFamilyNames().join(', ');
        return this.#loadedFallbackFamiliesCss;
    }

    /**
     * Starts loading the fallback fonts that `text` needs, returning them. The others are never
     * fetched unless some text needs them.
     */
    addFallbackFontsForText(text: string): FontLoader[] {
        return fallbackFontUrlsForText(text).map((url) => this.addFont(url));
    }

    /** Whether a fallback font some text asked for is still loading. */
    get hasFallbackFontsLoading(): boolean {
        return FALLBACK_FONT_URLS.some(
            (url) => this.#fonts.has(url) && !this.isFontDoneTryingToLoad(url),
        );
    }

    /**
     * Loads every fallback font, whether or not any text needs it. Only for warming a cache:
     * text assets load the fallbacks they need themselves.
     */
    async loadAllFallbackFonts(): Promise<FontLoader[]> {
        return Promise.all(
            FALLBACK_FONT_URLS.map(async (url) => this.getFontBySourceOnceLoaded(url)),
        );
    }

    /** Whether every fallback font some text asked for has loaded. */
    get isLoaded(): boolean {
        return FALLBACK_FONT_URLS.every(
            (url) => !this.#fonts.has(url) || this.#fonts.get(url)!.isLoaded,
        );
    }

    get isDoneTryingToLoad(): boolean {
        return !this.hasFallbackFontsLoading;
    }

    get canPlay(): boolean {
        return true;
    }

    _propagatePlay(): void {}
    _propagatePause(): void {}
}
